import { useState } from 'react';
import { Plus, GripVertical, Trash2 } from 'lucide-react';
import type { Block, BlockType } from '@/types';

interface BlockEditorProps {
  blocks: Block[];
  onChange: (blocks: Block[]) => void;
}

export function BlockEditor({ blocks, onChange }: BlockEditorProps) {
  const [showBlockMenu, setShowBlockMenu] = useState(false);

  const addBlock = (type: BlockType) => {
    const newBlock: any = {
      id: crypto.randomUUID(),
      type,
      order_index: blocks.length,
    };

    // Set defaults based on type
    switch (type) {
      case 'text':
        newBlock.content = '';
        break;
      case 'heading':
        newBlock.level = 2;
        newBlock.content = '';
        break;
      case 'image':
        newBlock.url = '';
        break;
      case 'video':
        newBlock.url = '';
        newBlock.controls = true;
        break;
      case 'list':
        newBlock.ordered = false;
        newBlock.items = [''];
        break;
      case 'divider':
        break;
      default:
        break;
    }

    onChange([...blocks, newBlock as Block]);
    setShowBlockMenu(false);
  };

  const updateBlock = (index: number, updates: Partial<Block>) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updates } as Block;
    onChange(newBlocks);
  };

  const removeBlock = (index: number) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    onChange(newBlocks);
  };

  const blockTypes: { type: BlockType; label: string; icon: string }[] = [
    { type: 'text', label: 'Text', icon: '📝' },
    { type: 'heading', label: 'Heading', icon: '📌' },
    { type: 'image', label: 'Image', icon: '🖼️' },
    { type: 'video', label: 'Video', icon: '🎥' },
    { type: 'list', label: 'List', icon: '📋' },
    { type: 'quote', label: 'Quote', icon: '💬' },
    { type: 'divider', label: 'Divider', icon: '➖' },
    { type: 'accordion', label: 'Accordion', icon: '📂' },
    { type: 'tabs', label: 'Tabs', icon: '📑' },
  ];

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {blocks.map((block, index) => (
        <div key={block.id} className="group mb-4 relative">
          <div className="absolute -left-12 top-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-2">
            <button className="p-1 hover:bg-gray-200 rounded">
              <GripVertical className="w-4 h-4 text-gray-400" />
            </button>
            <button
              onClick={() => removeBlock(index)}
              className="p-1 hover:bg-red-100 rounded text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <BlockRenderer
              block={block}
              onChange={(updates) => updateBlock(index, updates)}
            />
          </div>
        </div>
      ))}

      {/* Add Block Button */}
      <div className="relative">
        <button
          onClick={() => setShowBlockMenu(!showBlockMenu)}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors text-gray-600"
        >
          <Plus className="w-5 h-5" />
          Add Block
        </button>

        {showBlockMenu && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-2 grid grid-cols-3 gap-2">
            {blockTypes.map(({ type, label, icon }) => (
              <button
                key={type}
                onClick={() => addBlock(type)}
                className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="text-2xl">{icon}</span>
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BlockRenderer({
  block,
  onChange,
}: {
  block: Block;
  onChange: (updates: Partial<Block>) => void;
}) {
  switch (block.type) {
    case 'text':
      return (
        <textarea
          value={block.content}
          onChange={(e) => onChange({ content: e.target.value })}
          placeholder="Enter text..."
          className="w-full min-h-[100px] resize-y border-none focus:outline-none"
        />
      );

    case 'heading':
      return (
        <div>
          <select
            value={block.level}
            onChange={(e) => onChange({ level: Number(e.target.value) as any })}
            className="mb-2 text-sm border border-gray-300 rounded px-2 py-1"
          >
            {[1, 2, 3, 4, 5, 6].map((level) => (
              <option key={level} value={level}>
                Heading {level}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={block.content}
            onChange={(e) => onChange({ content: e.target.value })}
            placeholder="Enter heading..."
            className="w-full text-2xl font-bold border-none focus:outline-none"
          />
        </div>
      );

    case 'image':
      return (
        <div>
          <input
            type="text"
            value={block.url}
            onChange={(e) => onChange({ url: e.target.value })}
            placeholder="Image URL..."
            className="w-full mb-2 border border-gray-300 rounded px-3 py-2"
          />
          {block.url && (
            <img src={block.url} alt={block.alt || ''} className="max-w-full rounded" />
          )}
        </div>
      );

    case 'video':
      return (
        <div>
          <input
            type="text"
            value={block.url}
            onChange={(e) => onChange({ url: e.target.value })}
            placeholder="Video URL..."
            className="w-full mb-2 border border-gray-300 rounded px-3 py-2"
          />
          {block.url && (
            <video src={block.url} controls className="w-full rounded" />
          )}
        </div>
      );

    case 'list':
      return (
        <div>
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={block.ordered}
              onChange={(e) => onChange({ ordered: e.target.checked })}
            />
            <span className="text-sm">Ordered list</span>
          </label>
          {block.items.map((item, i) => (
            <input
              key={i}
              type="text"
              value={item}
              onChange={(e) => {
                const newItems = [...block.items];
                newItems[i] = e.target.value;
                onChange({ items: newItems });
              }}
              placeholder={`Item ${i + 1}...`}
              className="w-full mb-2 border border-gray-300 rounded px-3 py-2"
            />
          ))}
          <button
            onClick={() => onChange({ items: [...block.items, ''] })}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            + Add item
          </button>
        </div>
      );

    case 'divider':
      return <hr className="border-gray-300" />;

    default:
      return (
        <div className="text-gray-500 italic">
          {block.type} block (not yet implemented)
        </div>
      );
  }
}
